import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Workflow, Plus, X, Play, PlayCircle, Trash2, ShieldAlert, CheckCircle2,
  Clock, Loader2, Ban, KeyRound, FileDown, ChevronRight, Server, AlertTriangle,
  UserCheck, ShieldCheck, LayoutTemplate, Timer, FileSpreadsheet, FileText,
  Inbox, Mail, Zap,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import { CONNECTOR_META, JOB_STATUS, TASK_STATUS, Pill, SlaBadge, StatCard } from "./orchestration/shared";
import NewJobModal from "./orchestration/NewJobModal";
import FromResponseModal from "./orchestration/FromResponseModal";
import TemplateModal from "./orchestration/TemplateModal";
import GovernanceModal from "./orchestration/GovernanceModal";
import UploadJobsModal from "./orchestration/UploadJobsModal";

export default function Orchestration() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [busyTask, setBusyTask] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showFrom, setShowFrom] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [exporting, setExporting] = useState("");
  const [view, setView] = useState("board");
  const [inbox, setInbox] = useState([]);
  const [mineOnly, setMineOnly] = useState(false);
  const [showGov, setShowGov] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [inboxBusy, setInboxBusy] = useState("");
  const { user } = useAuth();
  const myEmail = (user?.email || "").toLowerCase();
  const isAdmin = user?.role === "admin";
  const [entraLive, setEntraLive] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");

  const loadJobs = async () => {
    try { const { data } = await api.get("/orchestration/jobs"); setJobs(data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    loadJobs(); loadInbox();
    api.get("/orchestration/connectors").then(({ data }) => setEntraLive(!!data.entra_live)).catch(() => {});
    api.get("/orchestration/settings").then(({ data }) => setNotifyEmail(data.notify_email || "")).catch(() => {});
  }, []);

  // Live refresh of board + inbox so email approvals/MFA appear without a manual reload
  useEffect(() => {
    const iv = setInterval(() => {
      api.get("/orchestration/jobs").then(({ data }) => setJobs(data)).catch(() => {});
      if (view === "inbox") api.get("/orchestration/inbox").then(({ data }) => setInbox(data)).catch(() => {});
    }, 6000);
    return () => clearInterval(iv);
  }, [view]);

  const openJob = async (id) => {
    try { const { data } = await api.get(`/orchestration/jobs/${id}`); setDetail(data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  // Live refresh of the open job drawer (stops once the job is terminal) so
  // email approvals / MFA completions reflect in real time without refreshing.
  const detailId = detail?.id;
  const detailActive = detail && !["completed", "cancelled"].includes(detail.status);
  useEffect(() => {
    if (!detailId || !detailActive) return;
    const iv = setInterval(() => {
      api.get(`/orchestration/jobs/${detailId}`).then(({ data }) => setDetail(data)).catch(() => {});
    }, 4000);
    return () => clearInterval(iv);
  }, [detailId, detailActive]);

  const act = async (fn, okMsg) => {
    setBusyTask("x");
    try { const data = await fn(); setDetail(data); await loadJobs(); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusyTask(""); }
  };

  const runTask = (t) => act(() => api.post(`/orchestration/jobs/${detail.id}/tasks/${t.id}/run`).then((r) => r.data));
  const runAll = () => act(() => api.post(`/orchestration/jobs/${detail.id}/run-all`).then((r) => r.data), "Fan-out complete.");
  const resolveApproval = (t) => act(() => api.post(`/orchestration/jobs/${detail.id}/tasks/${t.id}/resolve-approval`).then((r) => r.data), "Approval granted.");
  const resolveMfa = (t) => act(() => api.post(`/orchestration/jobs/${detail.id}/tasks/${t.id}/resolve-mfa`).then((r) => r.data), "MFA approved.");
  const failTask = (t) => act(() => api.post(`/orchestration/jobs/${detail.id}/tasks/${t.id}/fail`).then((r) => r.data));
  const cancelJob = () => act(() => api.post(`/orchestration/jobs/${detail.id}/cancel`).then((r) => r.data), "Job cancelled.");

  const overrideSod = () => {
    const j = window.prompt("Justification for overriding this Segregation-of-Duties conflict (recorded in the audit trail):");
    if (!j || !j.trim()) return;
    act(() => api.post(`/orchestration/jobs/${detail.id}/sod-override`, { justification: j.trim() }).then((r) => r.data), "SoD override recorded.");
  };

  const exportEvidence = async (fmt) => {
    setExporting(fmt);
    try {
      const { data } = await api.get(`/orchestration/jobs/${detail.id}/export/${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = `job_${detail.id}_evidence.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); } finally { setExporting(""); }
  };

  const deleteJob = async (id) => {
    try { await api.delete(`/orchestration/jobs/${id}`); toast.success("Job deleted."); setDetail(null); loadJobs(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const loadInbox = async () => {
    try { const { data } = await api.get("/orchestration/inbox"); setInbox(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { if (view === "inbox") loadInbox(); }, [view]);

  const inboxApprove = async (item) => {
    setInboxBusy(item.task_id);
    const ep = item.kind === "approval" ? "resolve-approval" : "resolve-mfa";
    try {
      await api.post(`/orchestration/jobs/${item.job_id}/tasks/${item.task_id}/${ep}`);
      toast.success(item.kind === "approval" ? "Request approved." : "MFA approved.");
      await loadInbox(); await loadJobs();
    } catch (e) { toast.error(apiErr(e)); } finally { setInboxBusy(""); }
  };

  const notifyGate = async (item) => {
    setInboxBusy(item.task_id);
    const ep = item.kind === "approval" ? "notify-approver" : "notify-mfa";
    try {
      const { data } = await api.post(`/orchestration/jobs/${item.job_id}/tasks/${item.task_id}/${ep}`);
      toast.success(`${item.kind === "approval" ? "Approval" : "MFA"} request emailed to ${data.to}.`);
    } catch (e) { toast.error(apiErr(e)); } finally { setInboxBusy(""); }
  };

  const changeNotifyEmail = async () => {
    const e = window.prompt("Email address that receives ALL approval & MFA requests:", notifyEmail);
    if (!e || !e.trim()) return;
    try {
      const { data } = await api.put("/orchestration/settings", { email: e.trim() });
      setNotifyEmail(data.notify_email);
      toast.success(`Approval & MFA requests now emailed to ${data.notify_email}.`);
    } catch (err) { toast.error(apiErr(err)); }
  };

  const visibleInbox = mineOnly ? inbox.filter((i) => i.kind === "approval" && i.approver.toLowerCase() === myEmail) : inbox;
  const iAmApprover = (approver) => !!(approver || "").trim() && (approver || "").trim().toLowerCase() === myEmail;
  const stats = {
    overdue: jobs.filter((j) => j.sla_breached).length,
    at_risk: jobs.filter((j) => j.sla_at_risk && !j.sla_breached).length,
    in_progress: jobs.filter((j) => j.status === "in_progress").length,
    completed: jobs.filter((j) => j.status === "completed").length,
  };
  const appsAuto = jobs.reduce((n, j) => n + (j.task_done || 0), 0);
  const ticketsAutomated = jobs.length;
  const MANUAL_MINUTES_PER_APP = 20;
  const hoursSaved = Math.round((appsAuto * MANUAL_MINUTES_PER_APP) / 60);

  return (
    <div className="min-h-screen pb-20" data-testid="orchestration-page">
      <TopBar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2" style={{ color: "var(--burgundy)" }}>
              <Workflow className="w-7 h-7" /> Provisioning Orchestration
            </h1>
            <p className="text-neutral-500 mt-1 text-sm">
              Fan an access request out into per-application tasks and drive each connector to completion.
              {entraLive ? (
                <span className="ml-1 inline-flex items-center gap-1 text-green-700" data-testid="entra-status"><ShieldCheck className="w-3 h-3" /> Azure AD connector is LIVE.</span>
              ) : (
                <span className="ml-1 inline-flex items-center gap-1 text-amber-700" data-testid="entra-status"><Server className="w-3 h-3" /> Connectors run in mock mode.</span>
              )}
            </p>
          </div>
          {isAdmin ? (
            <div className="flex items-center gap-2">
              <button data-testid="governance-btn" onClick={() => setShowGov(true)} className="btn btn-outline">
                <ShieldCheck className="w-4 h-4" /> Governance
              </button>
              <button data-testid="from-template-btn" onClick={() => setShowTemplate(true)} className="btn btn-outline">
                <LayoutTemplate className="w-4 h-4" /> From template
              </button>
              <button data-testid="from-response-btn" onClick={() => setShowFrom(true)} className="btn btn-outline">
                <FileDown className="w-4 h-4" /> From response
              </button>
              <button data-testid="upload-jobs-btn" onClick={() => setShowUpload(true)} className="btn btn-outline">
                <FileDown className="w-4 h-4 rotate-180" /> Upload CSV
              </button>
              <button data-testid="new-job-btn" onClick={() => setShowNew(true)} className="btn btn-primary">
                <Plus className="w-4 h-4" /> New job
              </button>
            </div>
          ) : (
            <span data-testid="viewonly-badge" className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full bg-neutral-100 text-neutral-600">
              <ShieldCheck className="w-3.5 h-3.5" /> View-only (Operator)
            </span>
          )}
        </div>

        <div className="flex items-center gap-1 mt-6 border-b border-[hsl(var(--border))]">
          <button data-testid="tab-board" onClick={() => setView("board")}
            className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 transition-colors ${view === "board" ? "border-[var(--burgundy)] text-[var(--burgundy)]" : "border-transparent text-neutral-500 hover:text-neutral-700"}`}>
            Board
          </button>
          <button data-testid="tab-inbox" onClick={() => setView("inbox")}
            className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 transition-colors inline-flex items-center gap-1.5 ${view === "inbox" ? "border-[var(--burgundy)] text-[var(--burgundy)]" : "border-transparent text-neutral-500 hover:text-neutral-700"}`}>
            <Inbox className="w-4 h-4" /> Approvals
            {inbox.length > 0 && <span className="bg-[var(--burgundy)] text-white text-[10px] px-1.5 py-0.5 rounded-full">{inbox.length}</span>}
          </button>
        </div>

        {view === "board" && !loading && jobs.length > 0 && (
          <div className="mt-5 rounded-xl brand-band text-white px-5 py-3 flex flex-wrap items-center gap-x-8 gap-y-2" data-testid="volume-strip">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-300" />
              <span className="text-sm text-amber-100">Automated provisioning impact</span>
            </div>
            <div><span className="text-2xl font-bold" data-testid="metric-apps">{appsAuto}</span> <span className="text-sm text-white/70">apps provisioned in a single pass</span></div>
            <div><span className="text-2xl font-bold" data-testid="metric-hours">~{hoursSaved}</span> <span className="text-sm text-white/70">analyst hours saved vs manual</span></div>
            <div><span className="text-2xl font-bold" data-testid="metric-tickets">{ticketsAutomated}</span> <span className="text-sm text-white/70">tickets orchestrated</span></div>
            <span className="text-[11px] text-white/40 ml-auto self-end">assumes ~{MANUAL_MINUTES_PER_APP} min/app manually</span>
          </div>
        )}

        {view === "board" && !loading && jobs.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4" data-testid="sla-dashboard">
            <StatCard label="Overdue" value={stats.overdue} cls="text-red-700" bg="bg-red-50" icon={AlertTriangle} testid="stat-overdue" />
            <StatCard label="At risk (<2h)" value={stats.at_risk} cls="text-amber-700" bg="bg-amber-50" icon={Timer} testid="stat-at-risk" />
            <StatCard label="In progress" value={stats.in_progress} cls="text-neutral-700" bg="bg-neutral-100" icon={Loader2} testid="stat-in-progress" />
            <StatCard label="Completed" value={stats.completed} cls="text-green-700" bg="bg-green-50" icon={CheckCircle2} testid="stat-completed" />
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20 text-[var(--burgundy)]"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : view === "inbox" ? (
          <div className="mt-6" data-testid="inbox-view">
            {isAdmin && (
              <div className="card-surface p-3 mb-3 flex flex-wrap items-center justify-between gap-2" data-testid="notify-email-bar">
                <p className="text-xs text-neutral-600 inline-flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-[var(--burgundy)]" />
                  All approval &amp; MFA requests are emailed to <b className="text-neutral-800" data-testid="notify-email-value">{notifyEmail || "—"}</b>
                </p>
                <button data-testid="change-notify-email-btn" onClick={changeNotifyEmail} className="btn btn-outline !py-1 !px-3 text-xs">
                  Change address
                </button>
              </div>
            )}
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-neutral-500">Tasks paused for a human decision across all active jobs.</p>
              <label className="text-xs text-neutral-600 inline-flex items-center gap-1.5">
                <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} data-testid="inbox-mine-toggle" /> Assigned to me
              </label>
            </div>
            <div className="space-y-2">
              {visibleInbox.map((i) => (
                <div key={i.task_id} className="card-surface p-4 flex items-center justify-between gap-3" data-testid={`inbox-item-${i.task_id}`}>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Pill map={TASK_STATUS} status={i.kind === "approval" ? "awaiting_approval" : "awaiting_mfa"} />
                      <span className="font-semibold text-neutral-800 truncate">{i.app_name}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${CONNECTOR_META[i.connector]?.cls}`}>{CONNECTOR_META[i.connector]?.label}</span>
                    </div>
                    <p className="text-xs text-neutral-500 mt-1">
                      {i.job_title} · requestor {i.requestor || "—"}
                      {i.kind === "approval" && i.approver && <span> · approver <b>{i.approver}</b></span>}
                    </p>
                  </div>
                  {isAdmin && (
                  <div className="flex items-center gap-2">
                    <button data-testid={`inbox-notify-${i.task_id}`} onClick={() => notifyGate(i)} disabled={!!inboxBusy} className="btn btn-outline !py-1.5 !px-3 text-xs whitespace-nowrap" title={`Email the ${i.kind === "approval" ? "approval" : "MFA"} request`}>
                      <Mail className="w-3.5 h-3.5" /> Notify
                    </button>
                    {iAmApprover(i.approver) ? (
                      <button data-testid={`inbox-approve-${i.task_id}`} onClick={() => inboxApprove(i)} disabled={!!inboxBusy} className="btn btn-primary !py-1.5 !px-3 text-xs whitespace-nowrap">
                        {inboxBusy === i.task_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (i.kind === "approval" ? <UserCheck className="w-3.5 h-3.5" /> : <KeyRound className="w-3.5 h-3.5" />)}
                        {i.kind === "approval" ? "Approve" : "Approve MFA"}
                      </button>
                    ) : (
                      <span data-testid={`inbox-locked-${i.task_id}`} className="inline-flex items-center gap-1 text-[11px] text-neutral-400 whitespace-nowrap" title="Segregation of Duties — only the designated approver can action this">
                        <ShieldCheck className="w-3.5 h-3.5" /> Approver: {i.approver || "—"}
                      </span>
                    )}
                  </div>
                  )}
                </div>
              ))}
              {!visibleInbox.length && (
                <div className="text-center text-neutral-400 py-16 card-surface" data-testid="inbox-empty">
                  Nothing awaiting a decision{mineOnly ? " assigned to you" : ""}. You're all caught up.
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-6" data-testid="jobs-grid">
            {jobs.map((j) => (
              <button key={j.id} onClick={() => openJob(j.id)} data-testid={`job-card-${j.id}`}
                className="card-surface p-4 text-left hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-[var(--burgundy)] line-clamp-2">{j.title}</p>
                  <Pill map={JOB_STATUS} status={j.status} />
                </div>
                <p className="text-xs text-neutral-500 mt-1 truncate">{j.requestor || "—"}</p>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <SlaBadge job={j} />
                  {j.sod_violations?.length > 0 && (
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${j.sod_override ? "bg-neutral-100 text-neutral-500" : "bg-red-100 text-red-800"}`} data-testid="sod-badge">
                      <ShieldAlert className="w-3 h-3" /> SoD{j.sod_override ? " (overridden)" : ""}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-neutral-600">{j.task_done}/{j.task_total} apps provisioned</span>
                  <span className="text-[10px] uppercase tracking-wide text-neutral-400">{j.source}</span>
                </div>
                <div className="h-1.5 bg-neutral-100 rounded-full mt-1.5 overflow-hidden">
                  <div className="h-full bg-[var(--burgundy)] transition-all" style={{ width: `${j.task_total ? (j.task_done / j.task_total) * 100 : 0}%` }} />
                </div>
              </button>
            ))}
            {!jobs.length && (
              <div className="col-span-full text-center text-neutral-400 py-16 card-surface" data-testid="jobs-empty">
                No provisioning jobs yet. Create one manually or generate from a submitted response.
              </div>
            )}
          </div>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={(e) => { if (e.target === e.currentTarget) setDetail(null); }} data-testid="job-detail">
          <div className="w-full max-w-2xl bg-[var(--parchment)] h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="brand-band text-white px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <div className="min-w-0">
                <p className="text-xs text-amber-200 uppercase tracking-widest">Provisioning job</p>
                <p className="font-semibold truncate">{detail.title}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-white/80 hover:text-white" data-testid="close-job-detail-btn"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <Pill map={JOB_STATUS} status={detail.status} />
                <SlaBadge job={detail} />
                <span className="text-sm text-neutral-600">{detail.task_done}/{detail.task_total} completed</span>
                {detail.sla_due_at && <span className="text-xs text-neutral-400">due {new Date(detail.sla_due_at).toLocaleString()}</span>}
                <span className="text-xs text-neutral-400" data-testid="sla-policy-note">
                  · SLA starts {detail.sla_policy === "processing" ? "when processing begins" : "when ticket is raised (ITIL)"}
                </span>
              </div>

              {detail.sod_violations?.length > 0 && (
                <div className={`rounded-lg p-3 border ${detail.sod_override ? "bg-neutral-50 border-neutral-200" : "bg-red-50 border-red-200"}`} data-testid="sod-banner">
                  <div className="flex items-start gap-2">
                    <ShieldAlert className={`w-4 h-4 mt-0.5 ${detail.sod_override ? "text-neutral-400" : "text-red-600"}`} />
                    <div className="text-sm">
                      <p className={`font-semibold ${detail.sod_override ? "text-neutral-600" : "text-red-800"}`}>
                        Segregation-of-Duties conflict{detail.sod_override ? " — overridden" : ""}
                      </p>
                      {detail.sod_violations.map((v, i) => (
                        <p key={i} className="text-xs text-neutral-600 mt-0.5">• [{v.severity}] {v.rule}: {v.left} vs {v.right}. {v.detail}</p>
                      ))}
                      {detail.sod_override ? (
                        <p className="text-xs text-neutral-500 mt-1 italic">Override recorded: {detail.sod_justification}</p>
                      ) : isAdmin ? (
                        <button data-testid="sod-override-btn" onClick={overrideSod} disabled={!!busyTask} className="btn btn-outline !py-1 !px-2.5 text-xs mt-2 !text-red-700 !border-red-300 hover:!bg-red-100">
                          Review &amp; override (with justification)
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {isAdmin && (
                  <button data-testid="run-all-btn" onClick={runAll} disabled={!!busyTask || detail.status === "cancelled"} className="btn btn-primary">
                    <PlayCircle className="w-4 h-4" /> Run all pending
                  </button>
                )}
                <button data-testid="export-csv-btn" onClick={() => exportEvidence("csv")} disabled={!!exporting} className="btn btn-outline">
                  {exporting === "csv" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Evidence CSV
                </button>
                <button data-testid="export-pdf-btn" onClick={() => exportEvidence("pdf")} disabled={!!exporting} className="btn btn-outline">
                  {exporting === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Evidence PDF
                </button>
                {isAdmin && detail.status !== "cancelled" && (
                  <button data-testid="cancel-job-btn" onClick={cancelJob} disabled={!!busyTask} className="btn btn-outline">
                    <Ban className="w-4 h-4" /> Cancel
                  </button>
                )}
                {isAdmin && (
                  <button data-testid="delete-job-btn" onClick={() => deleteJob(detail.id)} className="btn btn-outline !text-red-600 !border-red-200 hover:!bg-red-50">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                )}
              </div>

              <div className="space-y-3" data-testid="task-list">
                {detail.tasks.map((t) => (
                  <div key={t.id} className="card-surface p-4" data-testid={`task-${t.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-neutral-800 truncate">{t.app_name}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full ${CONNECTOR_META[t.connector]?.cls}`}>{CONNECTOR_META[t.connector]?.label}</span>
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">{t.action}</span>
                          {t.target && <span className="text-[11px] text-neutral-400">→ {t.target}</span>}
                          {t.requires_approval && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 inline-flex items-center gap-1"><UserCheck className="w-3 h-3" /> approval</span>
                          )}
                          {(t.requires_mfa || t.connector === "rpa") && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> MFA</span>
                          )}
                        </div>
                      </div>
                      <Pill map={TASK_STATUS} status={t.status} />
                    </div>

                    {isAdmin && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {["pending", "failed"].includes(t.status) && (
                        <button data-testid={`run-task-${t.id}`} onClick={() => runTask(t)} disabled={!!busyTask} className="btn btn-outline !py-1.5 !px-3 text-xs">
                          <Play className="w-3.5 h-3.5" /> {t.status === "failed" ? "Retry" : "Run"}
                        </button>
                      )}
                      {t.status === "awaiting_approval" && (
                        iAmApprover(t.approver) ? (
                          <button data-testid={`resolve-approval-${t.id}`} onClick={() => resolveApproval(t)} disabled={!!busyTask} className="btn btn-primary !py-1.5 !px-3 text-xs">
                            <UserCheck className="w-3.5 h-3.5" /> Approve request
                          </button>
                        ) : (
                          <span data-testid={`approval-locked-${t.id}`} className="inline-flex items-center gap-1 text-xs text-neutral-500 px-2 py-1.5 rounded bg-neutral-100" title="Segregation of Duties — approval was emailed to the designated approver">
                            <ShieldCheck className="w-3.5 h-3.5" /> Awaiting approver: {t.approver || "—"}
                          </span>
                        )
                      )}
                      {t.status === "awaiting_mfa" && (
                        iAmApprover(t.approver) ? (
                          <button data-testid={`resolve-mfa-${t.id}`} onClick={() => resolveMfa(t)} disabled={!!busyTask} className="btn btn-primary !py-1.5 !px-3 text-xs">
                            <KeyRound className="w-3.5 h-3.5" /> Approve MFA
                          </button>
                        ) : (
                          <span data-testid={`mfa-locked-${t.id}`} className="inline-flex items-center gap-1 text-xs text-neutral-500 px-2 py-1.5 rounded bg-neutral-100" title="Segregation of Duties — MFA was emailed to the designated approver">
                            <ShieldCheck className="w-3.5 h-3.5" /> Awaiting approver: {t.approver || "—"}
                          </span>
                        )
                      )}
                      {["pending", "running", "awaiting_approval", "awaiting_mfa"].includes(t.status) && (
                        <button onClick={() => failTask(t)} disabled={!!busyTask} className="btn btn-outline !py-1.5 !px-3 text-xs !text-red-600 !border-red-200 hover:!bg-red-50">
                          Mark failed
                        </button>
                      )}
                    </div>
                    )}

                    {t.logs?.length > 0 && (
                      <details className="mt-3 group">
                        <summary className="text-xs text-neutral-500 cursor-pointer flex items-center gap-1">
                          <ChevronRight className="w-3 h-3 group-open:rotate-90 transition-transform" /> Connector log ({t.logs.length})
                        </summary>
                        <div className="mt-2 bg-neutral-900 text-neutral-100 rounded-md p-2.5 text-[11px] font-mono space-y-1 max-h-48 overflow-y-auto">
                          {t.logs.map((l, i) => (
                            <div key={i} className={l.level === "warn" ? "text-amber-300" : l.level === "error" ? "text-red-300" : "text-neutral-300"}>
                              <span className="text-neutral-500">{new Date(l.ts).toLocaleTimeString()} </span>{l.message}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                ))}
                {!detail.tasks.length && <p className="text-sm text-neutral-400 text-center py-6">This job has no application tasks.</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {showNew && <NewJobModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); loadJobs(); }} />}
      {showFrom && <FromResponseModal onClose={() => setShowFrom(false)} onCreated={() => { setShowFrom(false); loadJobs(); }} />}
      {showTemplate && <TemplateModal onClose={() => setShowTemplate(false)} onCreated={() => { setShowTemplate(false); loadJobs(); }} />}
      {showGov && <GovernanceModal onClose={() => setShowGov(false)} />}
      {showUpload && <UploadJobsModal onClose={() => setShowUpload(false)} onCreated={() => { loadJobs(); loadInbox(); }} />}
    </div>
  );
}

